import {
  DateFormatOptions,
  DocumentOrganizationKeys,
  FilterDocument,
  FilterDocumentFilters,
  FilterOptionKeys,
  Goal,
  goalOtherID,
  GroupByMode,
  ID,
  LifeArea,
  lifeAreaOtherID,
  noTagId,
  SortingMode,
  Tag,
  WeekDays,
} from '@griply/types';
import {
  countActiveFilters,
  isDefaultFilterDocument,
  isNumber,
} from '@griply/utils';
import isEqual from 'lodash/isEqual';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActiveFilter, FilterGroup } from 'shared/types/filter';
import {
  createFilters,
  createGroupingAndSorting,
  processDurationFilterToString,
} from 'shared/utils/filters';
import { createResetFilterDocumentGroup } from 'shared/utils/filters/create-reset-filter-document-group';

export type Options<
  GBM extends GroupByMode,
  SM extends SortingMode,
  F extends FilterOptionKeys,
> = {
  goals: Goal[];
  lifeAreas: LifeArea[];
  tags: Tag[];
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  defaultFilterDocument?: FilterDocument<GBM, SM, F>;
  supportedGroupByModes?: readonly GBM[];
  supportedSortingModes?: readonly SM[];
  supportedFilters?: readonly F[];
  labelOverrides?: Partial<Record<GroupByMode, string>>;
  hideNoGoal?: boolean;
};

export const useFilterState = <
  GBM extends GroupByMode,
  SM extends SortingMode,
  F extends FilterOptionKeys,
>(
  filterDocument: FilterDocument<GBM, SM, F>,
  {
    goals,
    lifeAreas,
    tags,
    dateFormat,
    weekStartsOn,
    defaultFilterDocument,
    supportedGroupByModes,
    supportedSortingModes,
    supportedFilters,
    labelOverrides,
    hideNoGoal,
  }: Options<GBM, SM, F>,
) => {
  const { t } = useTranslation();
  const activeGoals = useMemo(
    () => goals.filter((goal) => !goal.archivedAt),
    [goals],
  );

  const filters = useMemo<FilterGroup[]>(
    () => [
      ...createGroupingAndSorting({
        t,
        supportedGroupByModes,
        supportedSortingModes,
        labelOverrides,
      }),
      ...createFilters({
        t,
        lifeAreas,
        tags,
        goals: activeGoals,
        dateFormat,
        weekStartsOn,
        supportedFilters,
        hideNoGoal,
      }),
      ...(isDefaultFilterDocument(
        filterDocument,
        defaultFilterDocument,
        supportedFilters,
      )
        ? []
        : createResetFilterDocumentGroup({ t })),
    ],
    [
      t,
      supportedGroupByModes,
      supportedSortingModes,
      lifeAreas,
      tags,
      activeGoals,
      dateFormat,
      weekStartsOn,
      supportedFilters,
      filterDocument,
      defaultFilterDocument,
      labelOverrides,
      hideNoGoal,
    ],
  );

  const activeOptions = useMemo<ActiveFilter[]>(
    () => [
      {
        id: DocumentOrganizationKeys.SortingMode,
        value: [filterDocument.sortingMode ?? SortingMode.DueDate],
      },
      {
        id: DocumentOrganizationKeys.GroupByMode,
        value: [filterDocument.groupByMode ?? GroupByMode.None],
      },
      ...Object.keys(filterDocument.filters ?? {}).reduce<ActiveFilter[]>(
        (acc, key) => {
          const val =
            filterDocument.filters?.[
              key as keyof typeof filterDocument.filters
            ];

          if (supportedFilters && !supportedFilters.includes(key as F)) {
            return acc;
          }

          const defaultVal =
            defaultFilterDocument?.filters?.[
              key as keyof typeof defaultFilterDocument.filters
            ];
          const isFilterDefault = isEqual(val, defaultVal);

          if (key === FilterOptionKeys.Duration) {
            const durationFilter =
              val as FilterDocumentFilters[FilterOptionKeys.Duration];
            const moreValue = durationFilter?.more?.value;
            const moreType = durationFilter?.more?.type;
            const lessValue = durationFilter?.less?.value;
            const lessType = durationFilter?.less?.type;
            const hasMore = isNumber(moreValue);
            const hasLess = isNumber(lessValue);

            if (hasMore || hasLess) {
              acc.push({
                id: key,
                value: [
                  hasMore
                    ? processDurationFilterToString(moreValue, moreType)
                    : '',
                  hasLess
                    ? processDurationFilterToString(lessValue, lessType)
                    : '',
                ],
                isDefault: isFilterDefault,
              });
            }
            return acc;
          }

          if (key === FilterOptionKeys.Deadline) {
            const deadlineFilter =
              val as FilterDocumentFilters[FilterOptionKeys.Deadline];
            if (deadlineFilter) {
              acc.push({
                id: key,
                value: [deadlineFilter.type],
                dateFilter: deadlineFilter,
                isDefault: isFilterDefault,
              });
            }
            return acc;
          }

          if (key === FilterOptionKeys.GoalActive) {
            const goalActiveFilter =
              val as FilterDocumentFilters[FilterOptionKeys.GoalActive];
            if (goalActiveFilter) {
              acc.push({
                id: key,
                value: [goalActiveFilter.type],
                dateFilter: goalActiveFilter,
                isDefault: isFilterDefault,
              });
            }
            return acc;
          }

          if (key === FilterOptionKeys.Date) {
            const dateFilter =
              val as FilterDocumentFilters[FilterOptionKeys.Date];
            if (dateFilter) {
              acc.push({
                id: key,
                value: [dateFilter.type],
                dateFilter,
                isDefault: isFilterDefault,
              });
            }
            return acc;
          }

          if (key === FilterOptionKeys.CompletedAt) {
            const completedAtFilter =
              val as FilterDocumentFilters[FilterOptionKeys.CompletedAt];
            if (completedAtFilter) {
              acc.push({
                id: key,
                value: [completedAtFilter.type],
                dateFilter: completedAtFilter,
                isDefault: isFilterDefault,
              });
            }
            return acc;
          }

          if (key === FilterOptionKeys.Tag) {
            const tagVal = val as FilterDocumentFilters[FilterOptionKeys.Tag];
            const ids = (tagVal?.include || []).filter((id) =>
              tags.find((tag) => tag.id === id),
            );

            if (tagVal?.noTag) {
              ids.push(noTagId);
            }

            if (!ids.length) {
              return acc;
            }

            acc.push({
              id: key,
              value: ids,
              isDefault: isFilterDefault,
            });

            return acc;
          }

          if (!val || !('include' in val) || !val.include?.length) {
            return acc;
          }

          if (key === FilterOptionKeys.LifeArea) {
            const include = val.include as ID[];
            const ids = (include || []).filter(
              (id) =>
                id === lifeAreaOtherID ||
                lifeAreas.find((lifeArea) => lifeArea.id === id),
            );

            if (ids.length) {
              acc.push({
                id: key,
                value: ids,
                isDefault: isFilterDefault,
              });
            }
            return acc;
          }

          if (key === FilterOptionKeys.Goal) {
            const include = val.include as ID[];
            const ids = (include || []).filter(
              (id) =>
                id === goalOtherID ||
                activeGoals.find((goal) => goal.id === id),
            );

            if (ids.length) {
              const goalVal =
                val as FilterDocumentFilters[FilterOptionKeys.Goal];
              const goalDefaultVal =
                defaultVal as FilterDocumentFilters[FilterOptionKeys.Goal];
              const isGoalDefault = isEqual(
                { include: goalVal?.include },
                { include: goalDefaultVal?.include },
              );
              acc.push({
                id: key,
                value: ids,
                isDefault: isGoalDefault,
              });
            }
            return acc;
          }

          if (key === FilterOptionKeys.Priority) {
            const include = val.include as ID[];
            if (include?.length) {
              acc.push({
                id: key,
                value: include,
                isDefault: isFilterDefault,
              });
            }
            return acc;
          }

          if (key === FilterOptionKeys.Type) {
            const include = val.include as ID[];
            if (include?.length) {
              acc.push({
                id: key,
                value: include,
                isDefault: isFilterDefault,
              });
            }
            return acc;
          }

          if (key === FilterOptionKeys.GoalImpact) {
            const include = val.include as ID[];
            if (include?.length) {
              acc.push({
                id: key,
                value: include,
                isDefault: isFilterDefault,
              });
            }
            return acc;
          }

          return acc;
        },
        [],
      ),
    ],
    [
      filterDocument,
      activeGoals,
      lifeAreas,
      tags,
      supportedFilters,
      defaultFilterDocument,
    ],
  );

  const activeFilters = useMemo(
    () =>
      countActiveFilters(filterDocument.filters, {
        defaultFilters: defaultFilterDocument?.filters,
        supportedFilters,
        goals: activeGoals,
        lifeAreas,
        tags,
      }),
    [
      filterDocument.filters,
      defaultFilterDocument,
      supportedFilters,
      activeGoals,
      lifeAreas,
      tags,
    ],
  );

  return {
    filters,
    activeFilters,
    activeOptions,
  };
};
